import React from 'react';
import { graphql } from 'gatsby';
import * as styles from './form-container-w-image.module.css';

import {
    KontentMedia,
    KontentRadioMultiChoice,
    KontentStringValue,
} from '../../../types/KontentProps';
import {
    getMediaData,
    optimizeKontentImageUrl,
    imagePreset,
    getDisplayDimensions,
    buildKontentSrcSet,
} from '../Global/DataUtils';
import LazyImage from '../Global/LazyImage';
import Jotform from '../Jotform';
type DesktopImagePositionOptions = 'left' | 'right';
type MobileImagePositionOptions = 'top' | 'bottom';

interface Props {
    desktop_image: KontentMedia;
    desktop_image_position?: KontentRadioMultiChoice<DesktopImagePositionOptions>;
    mobile_image: KontentMedia;
    mobile_image_position?: KontentRadioMultiChoice<MobileImagePositionOptions>;
    form_headline?: KontentStringValue;
    form_description?: KontentStringValue;
    form_link: KontentStringValue;
    anchor: KontentStringValue;
}

export const FormContainerWImage: React.FC<Props> = ({
    desktop_image,
    desktop_image_position,
    mobile_image,
    mobile_image_position,
    form_headline,
    form_description,
    form_link,
    anchor,
}) => {
    const DesktopImage = getMediaData(desktop_image);
    const MobileImage = getMediaData(mobile_image);
    const DesktopImgPosition =
        desktop_image_position?.value[0]?.codename == 'right';
    const MobileImgPosition =
        mobile_image_position?.value[0]?.codename == 'bottom';

    const containerClasses = [
        styles.container,
        DesktopImgPosition ? styles.imageRight : '',
        MobileImgPosition ? styles.imageDown : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={containerClasses}>
            <div className={styles.imageContainer}>
                {(DesktopImage?.url || MobileImage?.url) && (
                    <picture data-kontent-element-codename="desktop_image">
                        {MobileImage?.url && DesktopImage?.url && (
                            <source
                                media="(max-width: 576px)"
                                srcSet={buildKontentSrcSet(
                                    MobileImage.url,
                                    imagePreset.fullWidth.mobile.w,
                                    {
                                        fm: 'webp' as const,
                                        q: imagePreset.fullWidth.mobile.q,
                                    }
                                )}
                                sizes="100vw"
                                {...getDisplayDimensions(
                                    MobileImage.width,
                                    MobileImage.height,
                                    imagePreset.fullWidth.mobile.w
                                )}
                            />
                        )}
                        {DesktopImage?.url && MobileImage?.url && (
                            <source
                                media="(min-width: 577px) and (max-width: 991px)"
                                srcSet={buildKontentSrcSet(
                                    DesktopImage.url,
                                    imagePreset.fullWidth.tablet.w,
                                    {
                                        fm: 'webp' as const,
                                        q: imagePreset.fullWidth.tablet.q,
                                    }
                                )}
                                sizes="100vw"
                                {...getDisplayDimensions(
                                    DesktopImage.width,
                                    DesktopImage.height,
                                    imagePreset.fullWidth.tablet.w
                                )}
                            />
                        )}
                        <LazyImage
                            src={optimizeKontentImageUrl(
                                (DesktopImage?.url || MobileImage?.url)!,
                                DesktopImage?.url
                                    ? imagePreset.fullWidth.desktop
                                    : imagePreset.fullWidth.mobile
                            )}
                            srcSet={buildKontentSrcSet(
                                (DesktopImage?.url || MobileImage?.url)!,
                                DesktopImage?.url
                                    ? imagePreset.fullWidth.desktop.w
                                    : imagePreset.fullWidth.mobile.w,
                                {
                                    fm: 'webp' as const,
                                    q: imagePreset.fullWidth.desktop.q,
                                }
                            )}
                            sizes="(max-width: 991px) 100vw, 50vw"
                            alt={
                                DesktopImage?.description ||
                                DesktopImage?.name ||
                                MobileImage?.description ||
                                MobileImage?.name ||
                                ''
                            }
                            {...(DesktopImage?.url
                                ? getDisplayDimensions(
                                      DesktopImage.width,
                                      DesktopImage.height,
                                      imagePreset.fullWidth.desktop.w
                                  )
                                : getDisplayDimensions(
                                      MobileImage?.width,
                                      MobileImage?.height,
                                      imagePreset.fullWidth.mobile.w
                                  ))}
                        />
                    </picture>
                )}
            </div>
            <div className={styles.formContainer}>
                <h1
                    id={anchor.value ? anchor.value : ''}
                    data-kontent-element-codename="form_headline"
                >
                    {form_headline?.value}
                </h1>
                <p data-kontent-element-codename="form_description">
                    {form_description?.value}
                </p>
                <div className={styles.jotForm}>
                    <Jotform
                        formUrl={form_link.value}
                        formStyle="embed"
                        display={true}
                    />
                </div>
            </div>
        </div>
    );
};
export default FormContainerWImage;
